import LockIcon from "@mui/icons-material/Lock";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SyntheticEvent,
} from "react";

import type { PhoneScreen } from "./phoneMessage";
import {
  isValidUsStoredNumber,
  loadPersistedPhones,
  parseUsPhoneToE164,
  persistPhones,
  phoneOptionFromE164,
  type PhoneOption,
} from "./recipientPhones";
import {
  RecipientAccordion,
  RecipientAccordionDetails,
  RecipientAccordionSummary,
  RecipientSummaryLeading,
  RecipientTopBarChannelBadge,
  RecipientTopBarToggle,
  RecipientTopBarToggleButton,
} from "./RecipientView.styles";
import type {
  TextComposerFallbackChannel,
  TextComposerPrimaryChannel,
} from "../channel/channelDerivation";
import type { TextComposerFrameVariantOutput } from "../frame/frameOutput";
import type { PreviewFillParams } from "@/api/dragon-tiles/previewUserContext";
import type { RecognitionUser } from "@/api/profiles";
import type { FrameVariantConfig } from "@/types/dragon-tiles";

import { useSendTextPreviewTestSend } from "@/api/dragon-tiles/textPreviewTestSend";
import { useIsTextMessagingActive } from "@/api/siteCapabilities";
import { bannerTooltipSlotProps } from "@/components/bannerTooltip";
import { SmsNotEnabledBanner } from "@/components/SmsNotEnabledBanner";
import { EmailPreviewModalUserLookup } from "@/features/EmailEditor/components/EmailPreviewModalUserLookup";
import { ExpandMoreIcon, SmartphoneIcon } from "@/icons";

interface RecipientViewProps {
  siteId: string;
  testSendVariant: Pick<
    TextComposerFrameVariantOutput,
    "medium" | "content_json"
  > | null;
  config: FrameVariantConfig;
  primaryChannel: TextComposerPrimaryChannel;
  fallbackChannel: TextComposerFallbackChannel | null;
  viewMode: "primary" | "fallback";
  onViewModeChange: (nextMode: "primary" | "fallback") => void;
  /** Maps to test-send `customerId`; omit or empty when no contact is selected. */
  customerId?: string;
  selectedProfile?: RecognitionUser | null;
  onSelectedProfileChange?: (profile: RecognitionUser | null) => void;
  previewFillParams?: PreviewFillParams;
  screen?: PhoneScreen;
  onScreenChange?: (next: PhoneScreen) => void;
}

const SCREEN_OPTIONS = [
  { value: "app", label: "Text App" },
  { value: "lock", label: "Lock Screen" },
] as const satisfies ReadonlyArray<{ value: PhoneScreen; label: string }>;

function getChannelColor(
  theme: Theme,
  channel: TextComposerPrimaryChannel | TextComposerFallbackChannel,
) {
  if (channel === "RCS") {
    return theme.palette.success.main;
  }
  if (channel === "MMS") {
    return theme.palette.primary.main;
  }
  if (channel === "INVALID") {
    return theme.palette.text.secondary;
  }
  return theme.palette.info.main;
}

export function RecipientView({
  siteId,
  testSendVariant,
  config,
  primaryChannel,
  fallbackChannel,
  viewMode,
  onViewModeChange,
  customerId = "",
  selectedProfile = null,
  onSelectedProfileChange,
  previewFillParams,
  screen,
  onScreenChange,
}: RecipientViewProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [phones, setPhones] = useState<PhoneOption[]>(() =>
    loadPersistedPhones(siteId),
  );
  const [inputValue, setInputValue] = useState("");
  const sendTestMutation = useSendTextPreviewTestSend(siteId);
  // A site without text messaging activated can't send a text test (mirrors
  // the broadcast schedule gate — SMS-enabled only, not RCS).
  const isTextActive = useIsTextMessagingActive(siteId);

  useEffect(() => {
    setPhones(loadPersistedPhones(siteId));
  }, [siteId]);
  const hasFallback = fallbackChannel !== null;
  const activeChannel =
    viewMode === "fallback" && hasFallback ? fallbackChannel : primaryChannel;

  const allPhonesValid = useMemo(
    () => phones.every((p) => isValidUsStoredNumber(p.id)),
    [phones],
  );

  const canSend =
    testSendVariant !== null &&
    phones.length > 0 &&
    allPhonesValid &&
    primaryChannel !== "INVALID";

  const handlePhonesChange = useCallback(
    (next: PhoneOption[]) => {
      setPhones(next);
      persistPhones(siteId, next);
    },
    [siteId],
  );

  const handleSendTest = useCallback(() => {
    if (
      !testSendVariant ||
      phones.length === 0 ||
      !allPhonesValid ||
      !isTextActive
    ) {
      return;
    }
    sendTestMutation.mutate({
      site_id: siteId,
      medium: testSendVariant.medium,
      phone: phones.map((p) => p.id),
      content_json: testSendVariant.content_json,
      config,
      customerId: selectedProfile?.id ?? customerId,
      ...(previewFillParams ? { preview_fill_params: previewFillParams } : {}),
    });
  }, [
    allPhonesValid,
    config,
    customerId,
    isTextActive,
    phones,
    previewFillParams,
    selectedProfile,
    sendTestMutation,
    siteId,
    testSendVariant,
  ]);

  return (
    <RecipientAccordion
      disableGutters
      expanded={expanded}
      onChange={(_: SyntheticEvent, isExpanded: boolean) =>
        setExpanded(isExpanded)
      }
    >
      <RecipientAccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={(t) => ({
              fontSize: t.typography.fontSizeScale["1.25rem"],
              color: t.palette.primary.main,
            })}
          />
        }
      >
        <RecipientSummaryLeading>
          <SmartphoneIcon
            sx={(t) => ({
              fontSize: t.typography.fontSizeScale["1.25rem"],
              color: t.palette.text.primary,
            })}
          />
          <Typography variant="body1" fontWeight="medium">
            Recipient View
          </Typography>
          {hasFallback ? (
            <RecipientTopBarToggle>
              <RecipientTopBarToggleButton
                active={viewMode === "primary"}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewModeChange("primary");
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                sx={{
                  backgroundColor:
                    viewMode === "primary"
                      ? getChannelColor(theme, primaryChannel)
                      : "transparent",
                }}
              >
                <Typography
                  variant="caption"
                  component="span"
                  sx={(t) => ({
                    color:
                      viewMode === "primary"
                        ? t.palette.primary.contrastText
                        : t.palette.text.secondary,
                  })}
                >
                  {primaryChannel}
                </Typography>
              </RecipientTopBarToggleButton>
              <RecipientTopBarToggleButton
                active={viewMode === "fallback"}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewModeChange("fallback");
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                sx={{
                  backgroundColor:
                    viewMode === "fallback"
                      ? getChannelColor(theme, fallbackChannel)
                      : "transparent",
                }}
              >
                <Typography
                  variant="caption"
                  component="span"
                  sx={(t) => ({
                    color:
                      viewMode === "fallback"
                        ? t.palette.primary.contrastText
                        : t.palette.text.secondary,
                  })}
                >
                  {fallbackChannel}
                </Typography>
              </RecipientTopBarToggleButton>
            </RecipientTopBarToggle>
          ) : (
            <RecipientTopBarChannelBadge
              sx={{ backgroundColor: getChannelColor(theme, activeChannel) }}
            >
              <Typography variant="caption" component="span" color="inherit">
                {activeChannel}
              </Typography>
            </RecipientTopBarChannelBadge>
          )}
          {screen !== undefined && onScreenChange !== undefined ? (
            <RecipientTopBarToggle>
              {SCREEN_OPTIONS.map(({ value, label }) => (
                <RecipientTopBarToggleButton
                  key={value}
                  active={screen === value}
                  onClick={(event) => {
                    event.stopPropagation();
                    onScreenChange(value);
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  sx={{ width: "auto", px: 3 }}
                >
                  <Typography
                    variant="caption"
                    component="span"
                    sx={(t) => ({
                      color:
                        screen === value
                          ? t.palette.primary.contrastText
                          : t.palette.text.secondary,
                    })}
                  >
                    {label}
                  </Typography>
                </RecipientTopBarToggleButton>
              ))}
            </RecipientTopBarToggle>
          ) : null}
        </RecipientSummaryLeading>
        <Typography
          variant="body1"
          color="primary"
          noWrap
          sx={{
            textTransform: "uppercase",
            fontWeight: "medium",
          }}
        >
          Send test
        </Typography>
      </RecipientAccordionSummary>
      <RecipientAccordionDetails>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              alignItems: "flex-start",
            }}
          >
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              sx={{ flex: 1, minWidth: 0 }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              value={phones}
              onChange={(_, newValue) => {
                const normalized = newValue
                  .map((v): PhoneOption | null => {
                    if (typeof v === "string") {
                      const id = parseUsPhoneToE164(v);
                      return id ? phoneOptionFromE164(id) : null;
                    }
                    if (
                      v &&
                      typeof v.id === "string" &&
                      isValidUsStoredNumber(v.id)
                    ) {
                      return phoneOptionFromE164(v.id);
                    }
                    return null;
                  })
                  .filter((v): v is PhoneOption => v !== null);

                handlePhonesChange(normalized);
              }}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
              }}
              filterOptions={(_, params) => {
                const candidate = parseUsPhoneToE164(params.inputValue);
                if (candidate) {
                  return [phoneOptionFromE164(candidate)];
                }
                return [];
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Enter phone numbers"
                  size="small"
                  helperText="Enter phone numbers in any format (e.g., 5554443333, +1-555-444-3333)"
                  inputProps={{
                    ...params.inputProps,
                    type: "tel",
                    inputMode: "tel",
                    autoComplete: "off",
                    "data-1p-ignore": true,
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.name}
                    key={option.id}
                    size="small"
                  />
                ))
              }
              isOptionEqualToValue={(option, val) => {
                if (typeof option === "string" || typeof val === "string") {
                  return option === val;
                }
                return option.id === val.id;
              }}
            />
            <Tooltip
              title={
                !isTextActive ? <SmsNotEnabledBanner siteId={siteId} /> : ""
              }
              disableHoverListener={isTextActive}
              slotProps={bannerTooltipSlotProps}
            >
              <span style={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    !canSend || !isTextActive || sendTestMutation.isPending
                  }
                  startIcon={
                    !isTextActive ? <LockIcon fontSize="small" /> : undefined
                  }
                  onClick={() => handleSendTest()}
                  sx={{ flexShrink: 0 }}
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          </Box>
          <EmailPreviewModalUserLookup
            siteId={siteId}
            onCommittedPreviewUserChange={onSelectedProfileChange}
          />
        </Box>
      </RecipientAccordionDetails>
    </RecipientAccordion>
  );
}
